import * as React from "react";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  ListItemText,
  Tooltip,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchCustomers } from "../../api/customers";

const StyledFormControl = styled(FormControl)({
  width: "100%",
  "& .MuiInputLabel-root": {
    color: "#A0A3BD",
    fontSize: "1rem",
    transform: "translate(14px, 12px) scale(1)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#A0A3BD",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(1)",
    fontSize: "0.75rem",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#1A1C27",
    overflow: "hidden",
    "& fieldset": {
      borderRadius: "8px",
      borderColor: "#FFFFFF80",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#FFFFFF80",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#FFFFFF80",
      boxShadow: "none",
    },
  },
  "& .MuiSelect-select": {
    padding: "10px",
    color: "white",
    lineHeight: "20px",
    backgroundColor: "transparent",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    height: "38px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    color: "white",
    right: 12,
  },
});

const StyledMenuItem = styled(MenuItem)({
  backgroundColor: "white",
  color: "black",
  paddingTop: 10,
  paddingBottom: 10,
  borderRadius: "8px",
  margin: "8px 8px",
  "&:hover": {
    backgroundColor: "#627CFB1A",
    color: "black",
  },
  "&.Mui-selected": {
    backgroundColor: "#4C63D7 !important",
    color: "white",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "#4C63D7 !important",
    color: "white",
  },
  "&.Mui-focusVisible": {
    backgroundColor: "inherit",
  },
  "& .MuiListItemText-primary": {
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

function truncate(text, maxLength = 20) {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "…" : text;
}

export default function SideBarAccountSelector({
  customers = [],
  profile,
  activeCustomer,
}) {
  const [value, setValue] = React.useState("");

  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedAdminSearchInput, setDebouncedAdminSearchInput] = React.useState("");
  const [adminOptions, setAdminOptions] = React.useState([]);
  const [adminLoading, setAdminLoading] = React.useState(false);

  const [open, setOpen] = React.useState(false);
  const [hover, setHover] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = profile?.type === "admin";

  React.useEffect(() => {
    if (!isAdmin) return;

    // Only debounce/search while the dropdown is open.
    // When closed, we display the selected label in the input, which should not
    // trigger backend searches.
    if (!open) {
      setDebouncedAdminSearchInput("");
      return;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedAdminSearchInput(searchInput);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isAdmin, open, searchInput]);

  const routeCustomerId = React.useMemo(() => {
    const pathname = location?.pathname || "";
    const match = pathname.match(/^\/client\/([^/]+)(?:\/|$)/);
    return match?.[1] || null;
  }, [location?.pathname]);

  React.useEffect(() => {
    // If we're currently on a /client/<id>/... route, reflect that selection.
    // This makes the selector work for staff/customer-backend switching.
    if (routeCustomerId) {
      const stored = sessionStorage.getItem("selectedCustomerId");
      if (stored !== routeCustomerId) {
        sessionStorage.setItem("selectedCustomerId", routeCustomerId);
        localStorage.removeItem("dashboardDate");
        sessionStorage.setItem("adjustedView", JSON.stringify(false));

        window.dispatchEvent(new Event("dashboardDate-changed"));
        window.dispatchEvent(
          new CustomEvent("adjustedView-changed", { detail: false })
        );
        window.dispatchEvent(new Event("selectedCustomerId-changed"));
      }
      setValue(routeCustomerId);
      return;
    }

    // Admins do not have a pre-scoped customer list; selection comes from search.
    if (isAdmin) return;

    if (!customers.length) return;

    // Check localStorage first
    const stored = sessionStorage.getItem("selectedCustomerId");
    let chosenCustomer = null;

    if (stored) {
      chosenCustomer = customers.find((c) => c.id.toString() === stored);
    }

    if (!chosenCustomer) {
      chosenCustomer = customers.find((c) => !c.is_demo) || customers[0];
      sessionStorage.setItem("selectedCustomerId", chosenCustomer.id);
      localStorage.removeItem("dashboardDate");
      sessionStorage.setItem("adjustedView", JSON.stringify(false));

      window.dispatchEvent(new Event("dashboardDate-changed"));

      window.dispatchEvent(
        new CustomEvent("adjustedView-changed", { detail: false })
      );

      window.dispatchEvent(new Event("selectedCustomerId-changed"));
    }

    setValue(chosenCustomer.id.toString());
  }, [customers, isAdmin, routeCustomerId]);

  const selectedCustomer = customers.find((c) => c.id.toString() === value);

  // Keep the input displaying the current selection when not actively searching.
  React.useEffect(() => {
    if (!isAdmin) return;
    if (open) return;

    const currentId = routeCustomerId || value;
    const selectedOption =
      adminOptions.find((o) => String(o.value) === String(currentId)) ||
      (activeCustomer?.id
        ? { value: String(activeCustomer.id), label: activeCustomer.name }
        : null);
    setSearchInput(selectedOption?.label || "");
  }, [
    activeCustomer?.id,
    activeCustomer?.name,
    adminOptions,
    isAdmin,
    open,
    routeCustomerId,
    value,
  ]);

  // Admin-only: async search against /api/v2/customers/ (namespace middleware should scope results).
  const lastAdminFetchKeyRef = React.useRef(null);
  React.useEffect(() => {
    if (!isAdmin) return;

    // Only hit the backend when the user is actively searching/opening the selector.
    if (!open && !debouncedAdminSearchInput) return;

    const normalized = debouncedAdminSearchInput
      ? debouncedAdminSearchInput.charAt(0).toUpperCase() +
        debouncedAdminSearchInput.slice(1)
      : "";

    const currentId = routeCustomerId || value;
    const fetchKey = JSON.stringify({
      normalized,
      namespace: "new",
      currentId: currentId ? String(currentId) : "",
      activeCustomerId: activeCustomer?.id ? String(activeCustomer.id) : "",
      activeCustomerName: activeCustomer?.name ? String(activeCustomer.name) : "",
    });

    if (lastAdminFetchKeyRef.current === fetchKey) return;
    lastAdminFetchKeyRef.current = fetchKey;

    let mounted = true;
    setAdminLoading(true);

    const params = { page_size: 15, namespace: "new" };
    if (normalized) params.search = normalized;

    // Admin mode: do not key this effect on the full profile object (it can be unstable).
    // Auth is cookie-based; fetchCustomers only uses profile to add having_profile for client/accountant.
    fetchCustomers(null, params, true)
      .then((results) => {
        if (!mounted) return;
        let next = Array.isArray(results) ? results : [];

        // Ensure current customer is always selectable.
        const currentId = routeCustomerId || value;
        if (currentId && activeCustomer?.id && String(activeCustomer.id) === String(currentId)) {
          const has = next.some((o) => String(o.value) === String(activeCustomer.id));
          if (!has) {
            next = next.concat({ value: activeCustomer.id, label: activeCustomer.name });
          }
        }

        setAdminOptions(next);
      })
      .finally(() => {
        if (!mounted) return;
        setAdminLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    activeCustomer?.id,
    activeCustomer?.name,
    debouncedAdminSearchInput,
    isAdmin,
    open,
    routeCustomerId,
    value,
  ]);

  const applySelection = (id, { navigateIfNotInClientRoute = false } = {}) => {
    if (!id) return;
    const nextId = String(id);
    setValue(nextId);

    sessionStorage.setItem("selectedCustomerId", nextId);
    localStorage.removeItem("dashboardDate");
    sessionStorage.setItem("adjustedView", JSON.stringify(false));

    window.dispatchEvent(new Event("dashboardDate-changed"));
    window.dispatchEvent(
      new CustomEvent("adjustedView-changed", { detail: false })
    );
    window.dispatchEvent(new Event("selectedCustomerId-changed"));

    // When the UI is currently scoped to a specific customer via /client/<id>/...,
    // update the route so the dashboard actually switches customers.
    const pathname = location?.pathname || "";
    if (pathname.startsWith("/client/")) {
      const nextPathname = pathname.replace(
        /^\/client\/[^/]+/,
        `/client/${nextId}`
      );
      navigate({
        pathname: nextPathname,
        search: location?.search || "",
        hash: location?.hash || "",
      });
      return;
    }

    // Only admins should deep-link into a customer route from elsewhere.
    if (navigateIfNotInClientRoute) {
      navigate(`/client/${nextId}`);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const tooltipPlacement = isMobile ? "bottom" : "right";

  if (isAdmin) {
    const currentId = routeCustomerId || value;
    const selectedOption =
      adminOptions.find((o) => String(o.value) === String(currentId)) ||
      (activeCustomer?.id
        ? { value: activeCustomer.id, label: activeCustomer.name }
        : null);

    return (
      <Autocomplete
        options={adminOptions}
        value={selectedOption}
        onChange={(_, nextOption) => {
          if (!nextOption?.value) return;
          setSearchInput(nextOption?.label || "");
          applySelection(nextOption.value, { navigateIfNotInClientRoute: true });
        }}
        inputValue={searchInput}
        onInputChange={(_, nextInput, reason) => {
          // Prevent MUI from overwriting the user's typed search with the selected label.
          if (reason === "input" || reason === "clear") {
            setSearchInput(nextInput || "");
          }
        }}
        open={open}
        onOpen={() => {
          setOpen(true);
          setSearchInput("");
        }}
        onClose={() => setOpen(false)}
        loading={adminLoading}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(opt, val) => String(opt?.value) === String(val?.value)}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Account"
            size="small"
            InputLabelProps={{
              sx: { color: "#A0A3BD" },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {adminLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              sx: {
                borderRadius: "8px",
                backgroundColor: "#1A1C27",
                color: "white",
                "& fieldset": {
                  borderRadius: "8px",
                  borderColor: "#FFFFFF80",
                },
                "&:hover fieldset": {
                  borderColor: "#FFFFFF80",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FFFFFF80",
                },
              },
            }}
          />
        )}
      />
    );
  }

  return (
    <StyledFormControl variant="outlined">
      <InputLabel>Account</InputLabel>

      <Tooltip
        title={selectedCustomer ? selectedCustomer.name : ""}
        placement={tooltipPlacement}
        arrow
        open={!open && hover}
        slotProps={{
          tooltip: {
            sx: {
              height: "32px",
              borderRadius: "4px",
              border: "1px solid #F0F0F0",
              padding: "6px 8px",
              background: "#FFFFFF",
              boxShadow: "0px 0px 20px 0px #00000040",
              fontSize: "14px",
              color: "#000",
              display: "flex",
              alignItems: "center",
            },
          },
          popper: {
            sx: {
              pointerEvents: open ? "none" : "auto",
              visibility: open ? "hidden" : "visible",
            },
          },
          arrow: {
            sx: {
              color: "#FFFFFF",
              "&:before": { border: "1px solid #F0F0F0" },
            },
          },
        }}
      >
        <Select
          label="Account"
          value={value}
          onChange={(e) => {
            const nextId = e.target.value;
            if (!nextId) return;
            applySelection(nextId);
          }}
          IconComponent={KeyboardArrowDownIcon}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => {
            setOpen(false);
            setHover(false);
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            PaperProps: {
              sx: {
                borderRadius: "8px",
                mt: 1,
                maxHeight: 280,
                backgroundColor: "white",
                boxShadow: "0px 4px 32px 0px #00000033",
                px: 0,
                overflowY: "auto",

                "&::-webkit-scrollbar": {
                  width: "15px",
                  background: "var(--_native-scrollbar-bg, #EEEEEE)",
                  borderLeft: "1px solid var(--divider, #00000014)",
                  borderTopRightRadius: "8px",
                  borderBottomRightRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "var(--grey-400, #BDBDBD)",
                  borderRadius: "100px",
                  border: "4px solid transparent",
                  backgroundClip: "content-box",
                },
              },
            },
            MenuListProps: { sx: { py: 0 } },
          }}
          renderValue={() => truncate(selectedCustomer ? selectedCustomer.name : "")}
        >
          {customers.map((customer) => (
            <StyledMenuItem key={customer.id} value={customer.id.toString()}>
              <ListItemText primary={customer.name} />
            </StyledMenuItem>
          ))}
        </Select>
      </Tooltip>
    </StyledFormControl>
  );
}
